import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Wallet, IndianRupee, TrendingUp, Calendar, 
  AlertCircle, CheckCircle
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import MemberLayout from '../../components/layouts/MemberLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';
import { Progress } from '../../components/ui/progress';

const MemberDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      const response = await apiClient.get('/dashboard/member-stats');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <MemberLayout title="Dashboard">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="dashboard-card p-6">
              <div className="skeleton h-12 w-12 rounded-xl mb-4"></div>
              <div className="skeleton h-4 w-24 mb-2"></div>
              <div className="skeleton h-8 w-32"></div>
            </div>
          ))}
        </div>
      </MemberLayout>
    );
  }

  const statCards = [
    { 
      label: 'Total Capital', 
      value: data?.total_capital || 0, 
      icon: Wallet, 
      color: '#0A2540',
      bgColor: 'bg-[#0A2540]/5'
    },
    { 
      label: 'Total Contributions', 
      value: data?.total_contributions || 0, 
      icon: IndianRupee, 
      color: '#00C853',
      bgColor: 'bg-[#00C853]/10'
    },
    { 
      label: 'Pending Loan', 
      value: data?.pending_loan || 0, 
      icon: TrendingUp, 
      color: '#2962FF',
      bgColor: 'bg-[#2962FF]/10'
    },
    { 
      label: 'Total Penalties', 
      value: data?.total_penalties || 0, 
      icon: AlertCircle, 
      color: '#EF4444',
      bgColor: 'bg-red-50'
    },
  ];

  const contributionData = data?.contributions?.slice(0, 6).reverse().map(c => ({
    month: c.month,
    amount: c.amount
  })) || [];

  const loanProgress = data?.active_loan ? 
    (data.active_loan.paid_emis / (data.active_loan.paid_emis + data.active_loan.pending_emis)) * 100 : 0;

  return (
    <MemberLayout title="Dashboard" subtitle="Your financial overview">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            data-testid={`member-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            className="dashboard-card p-6"
          >
            <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center mb-4`}>
              <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
            </div>
            <p className="text-sm text-[#425466] mb-1">{stat.label}</p>
            <p className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {formatCurrency(stat.value)}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Loan Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="lg:col-span-2 dashboard-card p-6"
        >
          <h3 className="text-lg font-semibold text-[#0A2540] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Active Loan Status
          </h3>
          
          {data?.active_loan ? (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-[#F4F7FB]">
                  <p className="text-sm text-[#425466]">Principal</p>
                  <p className="text-xl font-bold text-[#0A2540]">{formatCurrency(data.active_loan.amount)}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#F4F7FB]">
                  <p className="text-sm text-[#425466]">Monthly EMI</p>
                  <p className="text-xl font-bold text-[#00C853]">{formatCurrency(data.active_loan.emi_amount)}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#F4F7FB]">
                  <p className="text-sm text-[#425466]">Remaining</p>
                  <p className="text-xl font-bold text-[#2962FF]">{formatCurrency(data.active_loan.remaining_amount)}</p>
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-[#425466]">Repayment Progress</span>
                  <span className="text-sm font-medium text-[#0A2540]">
                    {data.active_loan.paid_emis} of {data.active_loan.paid_emis + data.active_loan.pending_emis} EMIs paid
                  </span>
                </div>
                <Progress value={loanProgress} className="h-3" />
              </div>

              {/* Next EMI */}
              {data.emis?.filter(e => e.status !== 'paid')[0] && (
                <div className="p-4 rounded-xl bg-[#2962FF]/5 border border-[#2962FF]/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-[#2962FF]" />
                      <div>
                        <p className="font-medium text-[#0A2540]">Next EMI Due (4th Sunday)</p>
                        <p className="text-sm text-[#425466]">
                          {new Date(data.emis.filter(e => e.status !== 'paid')[0].due_date).toLocaleDateString('en-IN', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        <p className="text-xs text-red-500 mt-1">Late penalty: ₹100/day</p>
                      </div>
                    </div>
                    <p className="text-xl font-bold text-[#2962FF]">
                      {formatCurrency(data.emis.filter(e => e.status !== 'paid')[0].amount)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-[#425466]">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-[#00C853]" />
              <p className="text-lg font-medium text-[#0A2540]">No Active Loan</p>
              <p className="text-sm">You don't have any active loans at the moment</p>
            </div>
          )}
        </motion.div>

        {/* Contribution History Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="dashboard-card p-6"
        >
          <h3 className="text-lg font-semibold text-[#0A2540] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Contribution History
          </h3>
          
          {contributionData.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={contributionData}>
                  <defs>
                    <linearGradient id="colorContrib" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00C853" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00C853" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#425466" fontSize={10} />
                  <YAxis stroke="#425466" fontSize={10} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'rgba(255,255,255,0.95)', 
                      border: 'none', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [formatCurrency(value), 'Amount']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#00C853" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorContrib)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-[#425466]">
              No contribution data yet
            </div>
          )}
        </motion.div>
      </div>

      {/* EMI Schedule */}
      {data?.emis?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="mt-6 dashboard-card overflow-hidden"
        >
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              EMI Schedule
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F4F7FB]">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#425466] uppercase">EMI #</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#425466] uppercase">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#425466] uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#425466] uppercase">Principal</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#425466] uppercase">Interest</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#425466] uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.emis.map((emi) => (
                  <tr key={emi.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-[#0A2540]">{emi.emi_number}</td>
                    <td className="px-6 py-4 text-[#425466]">
                      {new Date(emi.due_date).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-6 py-4 font-semibold text-[#0A2540]">{formatCurrency(emi.amount)}</td>
                    <td className="px-6 py-4 text-[#425466]">{formatCurrency(emi.principal)}</td>
                    <td className="px-6 py-4 text-[#425466]">{formatCurrency(emi.interest)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        emi.status === 'paid' ? 'bg-green-100 text-green-600' :
                        emi.status === 'overdue' ? 'bg-red-100 text-red-600' :
                        'bg-yellow-100 text-yellow-600'
                      }`}>
                        {emi.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Recent Transactions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.7 }}
        className="mt-6 dashboard-card overflow-hidden"
      >
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Recent Transactions
          </h3>
        </div>
        <div className="divide-y divide-gray-100">
          {data?.transactions?.slice(0, 5).map((tx) => (
            <div key={tx.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  tx.type === 'contribution' ? 'bg-green-100' :
                  tx.type === 'loan_disbursement' ? 'bg-blue-100' :
                  tx.type === 'emi_payment' ? 'bg-purple-100' :
                  'bg-gray-100'
                }`}>
                  <IndianRupee className={`w-5 h-5 ${
                    tx.type === 'contribution' ? 'text-green-600' :
                    tx.type === 'loan_disbursement' ? 'text-blue-600' :
                    tx.type === 'emi_payment' ? 'text-purple-600' :
                    'text-gray-600'
                  }`} />
                </div>
                <div>
                  <p className="font-medium text-[#0A2540] capitalize">{tx.type.replace('_', ' ')}</p>
                  <p className="text-xs text-[#425466]">
                    {new Date(tx.created_at).toLocaleDateString('en-IN')}
                  </p>
                </div>
              </div>
              <p className={`font-semibold ${
                ['contribution', 'emi_payment'].includes(tx.type) ? 'text-green-600' : 'text-[#0A2540]'
              }`}>
                {['contribution', 'emi_payment'].includes(tx.type) ? '+' : '-'}{formatCurrency(tx.amount)}
              </p>
            </div>
          ))}
          {(!data?.transactions || data.transactions.length === 0) && (
            <div className="p-8 text-center text-[#425466]">No transactions yet</div>
          )}
        </div>
      </motion.div>
    </MemberLayout>
  );
};

export default MemberDashboard;
